import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Quote, X, Loader2, PenLine } from "lucide-react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const Testimonials = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [quote, setQuote] = useState("");
  const [role, setRole] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/testimonials").then((r) => setItems(r.data || [])).catch(() => {});
  }, []);

  const openModal = () => {
    if (!user) { toast.info("Please log in as a member to share your story."); navigate("/login"); return; }
    setQuote(""); setRole(""); setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!quote.trim()) { toast.error("Please tell us why you serve."); return; }
    setSubmitting(true);
    try {
      await api.post("/testimonials/submit", { quote: quote.trim(), role: role.trim() });
      toast.success("Thank you, patriot! Your story was submitted for review.");
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit. Please try again.");
    } finally { setSubmitting(false); }
  };

  if (!items.length) return null;

  return (
    <section className="bg-muted py-24 sm:py-32" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="mb-14 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div>
            <span className="text-sm uppercase tracking-[0.2em] font-semibold text-[#D90429]">Voices Of The Movement</span>
            <h2 className="font-display text-4xl sm:text-6xl uppercase mt-4 leading-[0.95]">
              Patriots <span className="text-[#D90429]">Speak</span>
            </h2>
          </div>
          <button
            data-testid="share-your-story-button"
            onClick={openModal}
            className="self-start inline-flex items-center gap-2 bg-[#0A1128] text-white px-6 py-3.5 uppercase tracking-[0.15em] text-xs font-bold hover:bg-[#1a2547] transition-colors duration-200 active:scale-95"
          >
            <PenLine className="w-4 h-4 text-[#F4A261]" /> Share Why You Serve
          </button>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((t, i) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.1 }}
              data-testid="testimonial-card"
              className="bg-white border border-slate-200 p-8 flex flex-col hover:border-[#D90429] transition-colors duration-200"
            >
              <Quote className="w-9 h-9 text-[#D90429] mb-5" />
              <p className="text-slate-700 leading-relaxed flex-1 italic">"{t.quote}"</p>
              <div className="flex items-center gap-4 mt-6 pt-6 border-t border-slate-100">
                {t.image_file_id ? (
                  <img
                    src={`${API}/public/files/${t.image_file_id}`}
                    alt={t.name}
                    className="w-12 h-12 object-cover rounded-full"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-[#0A1128] text-white flex items-center justify-center font-display text-lg">
                    {(t.name || "?").charAt(0).toUpperCase()}
                  </div>
                )}
                <div>
                  <div className="font-bold text-[#0A1128] uppercase tracking-wide text-sm">{t.name}</div>
                  {t.role && <div className="text-xs text-slate-500 uppercase tracking-[0.15em] mt-0.5">{t.role}</div>}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Submit modal */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="testimonial-submit-modal"
            className="fixed inset-0 z-[100] bg-black/80 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
            onClick={() => !submitting && setOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white w-full max-w-lg my-4" onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-[#0A1128] text-white p-6 flex items-start justify-between">
                <div>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-[#F4A261] font-bold">Patriots Speak</span>
                  <h3 className="font-display text-2xl uppercase mt-1 leading-tight">Why I Serve</h3>
                  <p className="text-white/60 text-sm mt-1">Share your story — approved quotes appear on our wall.</p>
                </div>
                <button data-testid="testimonial-submit-close" onClick={() => !submitting && setOpen(false)} className="text-white/60 hover:text-white"><X className="w-6 h-6" /></button>
              </div>
              <form onSubmit={submit} className="p-6 space-y-4">
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-1.5">Your Words *</label>
                  <textarea
                    data-testid="testimonial-quote-input"
                    required rows={4} maxLength={600}
                    value={quote}
                    onChange={(e) => setQuote(e.target.value)}
                    placeholder="Why do you stand with Patriot Power USA?"
                    className="w-full border border-slate-300 px-4 py-2.5 resize-none focus:outline-none focus:border-[#D90429]"
                  />
                  <p className="text-xs text-slate-400 mt-1">{quote.length}/600</p>
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-1.5">Your Role / Title (optional)</label>
                  <input
                    data-testid="testimonial-role-input"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    placeholder="e.g. Army Veteran · TX Chapter"
                    className="w-full border border-slate-300 px-4 py-2.5 focus:outline-none focus:border-[#D90429]"
                  />
                </div>
                <button
                  data-testid="testimonial-submit-button"
                  type="submit" disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#D90429] text-white px-6 py-3.5 uppercase tracking-[0.15em] text-sm font-bold hover:bg-[#b0031f] transition-colors duration-200 disabled:opacity-60"
                >
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : "Submit My Story"}
                </button>
                <p className="text-xs text-slate-400 text-center">Submissions are reviewed by our team before they appear on the wall.</p>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
